import { Divider, Grid, Link, Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { ETenderStatus } from 'types/tender'
import AssignmentIcon from '@mui/icons-material/Assignment'
import { formatTimeStampToDate } from 'hooks/date/formattedTime'
import { useMemo } from 'react'
import WorkspacesIcon from '@mui/icons-material/Workspaces'
import EditCalendarIcon from '@mui/icons-material/EditCalendar'
import PublicIcon from '@mui/icons-material/Public'
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom'
import DomainVerificationIcon from '@mui/icons-material/DomainVerification'
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents'
import GroupsIcon from '@mui/icons-material/Groups'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import DoDisturbIcon from '@mui/icons-material/DoDisturb'
import AlarmOnIcon from '@mui/icons-material/AlarmOn'
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline'
import MemoryIcon from '@mui/icons-material/Memory'
import { ITenderClientOverview } from 'types/tender/tenderClientOverview'
import { Link as RouteLink } from 'react-router-dom'

type Props = {
  dashboardContest: ITenderClientOverview,
  participating: boolean
}

export const DashboardContestCard = ({ dashboardContest, participating }: Props) => {
  const { t } = useTranslation()

  const tenderLink = `/portal/tender-participation/${dashboardContest.tenderId}`

  const icon = useMemo(() => {
    const props = { sx: { fontSize: 48, color: 'purple' } }

    switch (dashboardContest.tenderStatus) {
      case ETenderStatus.CREATED: {
        return <AssignmentIcon {...props} />
      }
      case ETenderStatus.PLANNING: {
        return <WorkspacesIcon {...props} />
      }
      case ETenderStatus.MODIFIED: {
        return <EditCalendarIcon {...props} />
      }
      case ETenderStatus.PUBLISHED: {
        return <PublicIcon {...props} />
      }
      case ETenderStatus.UNDER_EVALUATION: {
        return <HourglassBottomIcon {...props} />
      }
      case ETenderStatus.EVALUATION_SUBMITTED: {
        return <DomainVerificationIcon {...props} />
      }
      case ETenderStatus.WINNER_SELECTED: {
        return <EmojiEventsIcon {...props} />
      }
      case ETenderStatus.COMMISSION_EVALUATION: {
        return <GroupsIcon {...props} />
      }
      case ETenderStatus.COMMITTEE_REVIEW: {
        return <GroupsIcon {...props} />
      }
      case ETenderStatus.COMPLETED: {
        return <CheckCircleOutlineIcon {...props} />
      }
      case ETenderStatus.CANCELLED: {
        return <DoDisturbIcon {...props} />
      }
      case ETenderStatus.READY_FOR_BIDDING: {
        return <AlarmOnIcon {...props} />
      }
      case ETenderStatus.UNDER_BIDDING: {
        return <PlayCircleOutlineIcon {...props} />
      }
      case ETenderStatus.BIDDING_EVALUATION: {
        return <MemoryIcon {...props} />
      }
    }
  }, [dashboardContest.tenderStatus])

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={3}
      bgcolor="#FFFFFF"
      padding={2}
      borderRadius={1}
      marginTop={2}
      borderLeft={4}
      borderColor="primary.main"
      boxShadow={2}
    >
      {icon}
      <Stack width="100%" spacing={1}>
        <Link component={RouteLink} to={tenderLink} underline="hover">
          <Typography variant="subtitle2" fontWeight="bold" color="primary">
            {dashboardContest.tenderName}
          </Typography>
        </Link>

        <Divider flexItem />
        <Grid container>
          <Grid item xs={3}>
            <Typography variant="caption" fontWeight="bold">
              {t('organization')}:
            </Typography>
            <Typography variant="body2">
              {dashboardContest.tenderOrganizationName}
            </Typography>
          </Grid>
          <Grid item xs={2}>
            <Typography variant="caption" fontWeight="bold">
              {t('status')}:
            </Typography>
            <Typography variant="body2">
              {t(`tender-status-${dashboardContest.tenderStatus}`)}
            </Typography>
          </Grid>
          <Grid item xs={2}>
            <Typography variant="caption" fontWeight="bold">
              {t('procurementMethod')}:
            </Typography>
            <Typography variant="body2">
              {dashboardContest.tenderType ? t(dashboardContest.tenderType) : '-'}
            </Typography>
          </Grid>
          <Grid item xs={2}>
            <Typography variant="caption" fontWeight="bold">
              {t('startsAt')}:
            </Typography>
            <Typography variant="body2">
              {dashboardContest.tenderStartDate
                ? formatTimeStampToDate(dashboardContest.tenderStartDate)
                : '-'}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="caption" fontWeight="bold">
              {participating ? t('submitted-at') : t('documentSubmissionDeadline')}:
            </Typography>
            <Typography variant="body2">
              {participating ?
                formatTimeStampToDate(dashboardContest.submissionDate)
                : formatTimeStampToDate(dashboardContest.tenderEndDate)
              }
            </Typography>
          </Grid>
        </Grid>
      </Stack>
    </Stack>
  )
}
